/**
 * Date Formatting Utilities
 * Handles conversion between dd/mm/yyyy (display) and yyyy-mm-dd (backend)
 *
 * CALENDAR DATES vs TIMESTAMPS — pick the right pair.
 *
 * A calendar date (date_of_birth, validade, admissão) is a Postgres DATE. It reaches the
 * browser as "1990-05-15T00:00:00Z", where the time is padding, not information. Passing
 * that through `new Date(s).toLocaleDateString()` converts to the host timezone, so in
 * Brazil (UTC-3) every such date rendered ONE DAY EARLY. Use the formatCalendarDate*
 * helpers for those fields — they read the date lexically and do no timezone arithmetic,
 * so the answer is the same in every timezone.
 *
 * The formatDateTo* pair below applies local-timezone conversion, which is correct only
 * for real instants (created_at, an access event) and wrong for calendar dates.
 */

/** Matches the leading YYYY-MM-DD of a date-only string or an ISO timestamp. */
const CALENDAR_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s]|$)/;

/** Splits a calendar date into its parts, or null when the input is not one. */
function calendarParts(value: string | undefined | null): { year: string; month: string; day: string } | null {
  const match = CALENDAR_DATE_PATTERN.exec((value ?? '').trim());
  if (!match) return null;

  const [, year, month, day] = match;
  // Reject impossible dates (2024-13-45) without letting Date silently roll them over.
  const probe = new Date(`${year}-${month}-${day}T00:00:00Z`);
  if (isNaN(probe.getTime())) return null;
  if (probe.getUTCMonth() + 1 !== Number(month) || probe.getUTCDate() !== Number(day)) return null;

  return { year, month, day };
}

/**
 * Formats a CALENDAR date as dd/mm/yyyy. Never applies a timezone shift, never throws;
 * returns '' for anything it cannot read as a calendar date.
 */
export const formatCalendarDateToBR = (value: string | undefined | null): string => {
  const parts = calendarParts(value);
  return parts ? `${parts.day}/${parts.month}/${parts.year}` : '';
};

/**
 * Formats a CALENDAR date as yyyy-mm-dd for an <input type="date">. Never applies a
 * timezone shift, never throws; returns '' for anything it cannot read.
 */
export const formatCalendarDateForInput = (value: string | undefined | null): string => {
  const parts = calendarParts(value);
  return parts ? `${parts.year}-${parts.month}-${parts.day}` : '';
};

/**
 * Format date to Brazilian format (dd/mm/yyyy)
 *
 * TIMESTAMP semantics — converts to the host timezone. For a calendar date such as
 * date_of_birth this shifts the day; use formatCalendarDateToBR there instead.
 */
export const formatDateToBR = (dateString: string | undefined | null): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('pt-BR');
  } catch {
    return '';
  }
};

/**
 * Format date to ISO format (yyyy-mm-dd) for backend
 */
export const formatDateToISO = (dateString: string | undefined | null): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

/**
 * Parse dd/mm/yyyy string to yyyy-mm-dd for backend
 */
export const parseBRDateToISO = (brDate: string): string => {
  if (!brDate) return '';

  // Remove any non-digit characters
  const cleaned = brDate.replace(/\D/g, '');

  if (cleaned.length !== 8) return '';

  const day = cleaned.substring(0, 2);
  const month = cleaned.substring(2, 4);
  const year = cleaned.substring(4, 8);

  // Validate ranges
  const dayNum = parseInt(day, 10);
  const monthNum = parseInt(month, 10);
  const yearNum = parseInt(year, 10);

  if (dayNum < 1 || dayNum > 31) return '';
  if (monthNum < 1 || monthNum > 12) return '';
  if (yearNum < 1900 || yearNum > 2100) return '';

  return `${year}-${month}-${day}`;
};

/**
 * Parse yyyy-mm-dd to dd/mm/yyyy for display
 */
export const parseISODateToBR = (isoDate: string): string => {
  if (!isoDate) return '';

  const parts = isoDate.split('-');
  if (parts.length !== 3) return '';

  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
};

/**
 * Format date input with dd/mm/yyyy mask
 */
export const formatDateInput = (value: string): string => {
  // Remove any non-digit characters
  const cleaned = value.replace(/\D/g, '');

  // Apply mask: dd/mm/yyyy
  if (cleaned.length <= 2) {
    return cleaned;
  } else if (cleaned.length <= 4) {
    return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
  } else {
    return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
  }
};

/**
 * Validate dd/mm/yyyy date string
 */
export const isValidBRDate = (brDate: string): boolean => {
  if (!brDate) return false;

  const cleaned = brDate.replace(/\D/g, '');
  if (cleaned.length !== 8) return false;

  const day = parseInt(cleaned.substring(0, 2), 10);
  const month = parseInt(cleaned.substring(2, 4), 10);
  const year = parseInt(cleaned.substring(4, 8), 10);

  if (day < 1 || day > 31) return false;
  if (month < 1 || month > 12) return false;
  if (year < 1900 || year > 2100) return false;

  // Check if date is valid (e.g., not 31/02/2024)
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year &&
         date.getMonth() === month - 1 &&
         date.getDate() === day;
};
